import * as React from "react"
import { Toaster as SonnerPrimitive, toast as sonnerToast } from "sonner"
import type { ToasterProps } from "sonner"
import { CheckmarkFilled, ErrorFilled, WarningAltFilled, InformationFilled, Renew, Close } from "@carbon/icons-react"

const TOP_RIGHT_TOASTER_ID = "top-right-toaster"

export const topRightToast = {
  warning: (message: string, description?: string) => sonnerToast.warning(message, { toasterId: TOP_RIGHT_TOASTER_ID, description }),
  success: (message: string, description?: string) => sonnerToast.success(message, { toasterId: TOP_RIGHT_TOASTER_ID, description }),
  error: (message: string, description?: string) => sonnerToast.error(message, { toasterId: TOP_RIGHT_TOASTER_ID, description }),
  info: (message: string, description?: string) => sonnerToast.info(message, { toasterId: TOP_RIGHT_TOASTER_ID, description }),
}

const BASE_CLASSNAMES: NonNullable<NonNullable<ToasterProps["toastOptions"]>["classNames"]> = {
  toast:
    "group font-[family-name:var(--font-family--sans)] text-sm " +
    "bg-[var(--general-molecules-bg-bg-white)] " +
    "border border-[var(--general-border-solid-30)] " +
    "shadow-[var(--xs-x)_var(--xs-y)_var(--xs-blur)_var(--xs-spread)_var(--xs-color)] " +
    "rounded-[var(--semantic-rounded-lg)] " +
    "px-[var(--semantic-md)] py-[var(--semantic-xs)] " +
    "gap-[var(--semantic-xs)]",
  title:
    "font-[family-name:var(--font-family--sans)] text-[length:var(--font-size-s)] font-semibold " +
    "leading-[var(--line-height-s-heading)] tracking-[var(--letter-spacing-default)] " +
    "text-[var(--general-text-primary)]",
  description:
    "font-[family-name:var(--font-family--sans)] text-[length:var(--font-size-s)] font-normal " +
    "leading-[var(--line-height-s-para)] tracking-[0.07px] " +
    "text-[var(--general-text-secondary)]",
  actionButton:
    "!bg-transparent !text-[var(--ds-solid-neutral-12)] " +
    "text-[length:var(--font-size-xs)] font-medium " +
    "border border-[var(--general-border-solid-30)] " +
    "rounded-[var(--semantic-rounded-sm)] " +
    "px-[var(--semantic-xs)] py-[var(--semantic-2xs)] " +
    "hover:bg-[var(--ds-alpha-neutral-3)] transition-colors",
  cancelButton:
    "!bg-transparent !text-[var(--general-text-secondary)] " +
    "text-[length:var(--font-size-xs)] font-medium " +
    "border border-[var(--general-border-solid-30)] " +
    "rounded-[var(--semantic-rounded-sm)] " +
    "px-[var(--semantic-xs)] py-[var(--semantic-2xs)] " +
    "hover:bg-[var(--ds-alpha-neutral-3)] transition-colors",
  icon: "text-[var(--general-text-secondary)]",
  closeButton:
    "!bg-[var(--general-molecules-bg-bg-white)] " +
    "!border-[var(--general-border-solid-30)] " +
    "!text-[var(--general-text-secondary)] " +
    "hover:!text-[var(--ds-solid-neutral-12)] transition-colors",
  loading: "text-[var(--general-text-secondary)]",
  success: "!border-[var(--ds-solid-green-300)] !bg-[var(--ds-solid-green-50)]",
  error: "!border-[var(--general-border-destructive)] !bg-[var(--ds-solid-red-100)]",
  warning: "!border-[var(--ds-solid-yellow-300)] !bg-[var(--ds-solid-yellow-50)]",
}

const TOAST_ICONS: NonNullable<ToasterProps["icons"]> = {
  success: <CheckmarkFilled className="h-4 w-4 text-[var(--ds-solid-green-700)]" />,
  error: <ErrorFilled className="h-4 w-4 text-[var(--ds-solid-red-700)]" />,
  warning: <WarningAltFilled className="h-4 w-4 text-[var(--ds-solid-yellow-700)]" />,
  info: <InformationFilled className="h-4 w-4 text-[var(--general-text-secondary)]" />,
  loading: <Renew className="h-4 w-4 animate-spin text-[var(--general-text-secondary)]" />,
  close: <Close className="h-4 w-4 text-[var(--general-text-secondary)]" />,
}

// sonner ships its own [data-sonner-toaster] rule at z-index: 999999999, which floats
// toasts above every Dialog (Shadcn Dialog uses z-50). Override via inline style — inline
// styles win over that vendor stylesheet rule — so toasts sit above page content like
// MapContainer but below any open Dialog/modal.
const TOASTER_Z_INDEX = 40

const Toaster = React.forwardRef<
  React.ElementRef<typeof SonnerPrimitive>,
  ToasterProps
>(({ toastOptions, icons, style, ...props }, ref) => {
  const mergedClassNames = { ...BASE_CLASSNAMES, ...toastOptions?.classNames }
  const mergedToastOptions = { ...toastOptions, classNames: mergedClassNames }
  const mergedIcons = { ...TOAST_ICONS, ...icons }
  const mergedStyle = { zIndex: TOASTER_Z_INDEX, ...style }

  return (
    <>
      <SonnerPrimitive
        id={TOP_RIGHT_TOASTER_ID}
        position="top-right"
        offset={{ right: "24.5%", top: "8%" }}
        toastOptions={mergedToastOptions}
        icons={mergedIcons}
        style={mergedStyle}
      />
      <SonnerPrimitive
        ref={ref}
        position="bottom-right"
        toastOptions={mergedToastOptions}
        icons={mergedIcons}
        style={mergedStyle}
        {...props}
      />
    </>
  )
})
Toaster.displayName = "Toaster"

export { Toaster }
export { toast } from "sonner"
